import { useCallback, useEffect, useMemo, useState, type ComponentType, type CSSProperties, type ReactNode } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  AlertTriangle,
  ArrowRight,
  Banknote,
  Bell,
  BellOff,
  Check,
  CheckCheck,
  Clock,
  Inbox,
  Layers,
  Mail,
  ListChecks,
  Paperclip,
  Plus,
  RefreshCw,
  Settings2,
  SlidersHorizontal,
  UserRound,
  X,
} from "lucide-react";
import { useStore } from "../lib/store";
import { paletteColor } from "../lib/palette";
import { stageTone } from "../features/funnels/hub";
import {
  NOTICE_CATEGORIES,
  NOTICE_CATEGORY_LABEL,
  civilDate,
  type Notice,
  type NoticeCategory,
} from "../features/notifications/feed";
import {
  DEFAULT_NOTICE_VIEW,
  NOTICE_PERIODS,
  NOTICE_VIEWS,
  filterNotices,
  groupNoticesByDay,
  isNoticePeriodId,
  isNoticeViewId,
  summarizeNotices,
  type NoticePeriodId,
  type NoticeViewId,
} from "../features/notifications/inbox";
import {
  readPreference,
  type NotificationPreferences,
} from "../features/preferences/contracts";
import { PageHead, Toolbar, ToolbarSpacer } from "../ui/PageHead";
import { SideNav } from "../ui/SideNav";
import { Tabs } from "../ui/Tabs";
import { Button } from "../ui/Button";
import { IconButton } from "../ui/IconButton";
import { SearchInput } from "../ui/SearchInput";
import { CompactSelect } from "../ui/CompactSelect";
import { EmptyState } from "../ui/EmptyState";
import { Avatar } from "../ui/Avatar";
import { Dialog } from "../ui/Dialog";
import { Switch } from "../ui/Switch";
import { CosmosAiIcon } from "../ui/CosmosAiIcon";
import { appPath } from "../app/routes";
import "./notificacoes.css";

/**
 * Central de notificações.
 *
 * O feed é derivado do domínio a cada render: nenhuma linha nasce por observar
 * uma tarefa vencida. O que se grava é só a decisão do usuário — leu, resolveu
 * — e é por isso que "marcar como lida" não faz a pendência sumir.
 *
 * Ler e resolver são independentes de propósito, e resolver não conserta a
 * causa: a tarefa continua vencida até ser concluída. O painel de detalhe diz
 * isso em texto, em vez de deixar o usuário deduzir (AGENTS §18).
 *
 * Uma categoria silenciada nas preferências some do feed inteiro. A caixa dela
 * continua listada e marcada como silenciada: um zero sem explicação pareceria
 * defeito.
 */

const TIME_ZONE = "America/Sao_Paulo";

/** Ícones das abas, na ordem do protótipo. */
const VIEW_ICONS: Record<NoticeViewId, typeof Bell> = {
  todas: Bell,
  "nao-lidas": Mail,
  pendentes: AlertTriangle,
  resolvidas: Check,
};

/** A estrela de traço da IA não vem do lucide; o desenho é nosso. */
function AiRail({ size }: { size?: number }) {
  return <CosmosAiIcon size={size} variant="outline" />;
}

const CATEGORY_ICONS: Record<NoticeCategory, ComponentType<{ size?: number }>> = {
  ia: AiRail,
  tarefa: ListChecks,
  financeiro: Banknote,
  lead: UserRound,
  mensagem: Mail,
  automacao: AlertTriangle,
  anexo: Paperclip,
};

const CATEGORY_TONES: Record<NoticeCategory, string> = {
  ia: "var(--accent-2)",
  tarefa: "var(--accent)",
  financeiro: "var(--good)",
  lead: "var(--accent-2)",
  mensagem: "var(--proposal)",
  automacao: "var(--warn)",
  anexo: "var(--muted)",
};

const SEVERITY_TONE = {
  critico: "var(--crit)",
  atencao: "var(--warn)",
  informativo: "var(--muted)",
} as const;

const timeFormat = new Intl.DateTimeFormat("pt-BR", {
  timeZone: TIME_ZONE,
  hour: "2-digit",
  minute: "2-digit",
});

const fullFormat = new Intl.DateTimeFormat("pt-BR", {
  timeZone: TIME_ZONE,
  dateStyle: "long",
  timeStyle: "short",
});

/** Dia e mês curtos, no formato do protótipo: "16 set". */
const dayFormat = new Intl.DateTimeFormat("pt-BR", {
  timeZone: TIME_ZONE,
  day: "numeric",
  month: "short",
});

/**
 * Nome único do aviso para os controles.
 *
 * Três tarefas vencem hoje e os três avisos se chamam "Tarefa para hoje": um
 * botão chamado só pelo título deixaria quem usa leitor de tela sem saber qual
 * linha está acionando (AGENTS §13.1, §17). O detalhe carrega a pessoa e a
 * causa, e é ele que separa um do outro.
 */
function noticeName(notice: Notice): string {
  return [notice.title, notice.detail].filter(Boolean).join(" · ");
}

/** Hora quando é de hoje, dia e mês curtos quando não é. */
function stamp(notice: Notice, today: string): string {
  const moment = new Date(notice.occurredAt);
  if (Number.isNaN(moment.getTime())) return "—";
  if (civilDate(notice.occurredAt, TIME_ZONE) === today) return timeFormat.format(moment);
  // `Intl` devolve "16 de set."; o protótipo escreve "16 set".
  return dayFormat.format(moment).replace(" de ", " ").replace(".", "");
}

export function NotificacoesPage() {
  const store = useStore();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [now, setNow] = useState(() => new Date().toISOString());
  // O sino do topo abre as preferências por `?preferencias=1`; o parâmetro é
  // consumido na montagem e não fica na URL.
  const [settingsOpen, setSettingsOpen] = useState(() => params.get("preferencias") === "1");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date().toISOString()), 60_000);
    return () => window.clearInterval(timer);
  }, []);

  const view: NoticeViewId = isNoticeViewId(params.get("aba"))
    ? (params.get("aba") as NoticeViewId)
    : DEFAULT_NOTICE_VIEW;
  const period: NoticePeriodId = isNoticePeriodId(params.get("periodo"))
    ? (params.get("periodo") as NoticePeriodId)
    : "todos";
  const query = params.get("q") ?? "";
  const categoryParam = params.get("caixa");
  const category = NOTICE_CATEGORIES.includes(categoryParam as NoticeCategory)
    ? (categoryParam as NoticeCategory)
    : null;
  const funnels = useMemo(() => store.funnels.filter((funnel) => !funnel.archived), [store.funnels]);
  /** Cor da etapa pelo nome, no funil do aviso; fora dele, o cinza de apoio. */
  const stageColorOf = (funnelId: string | undefined, name: string) =>
    paletteColor(store.stages.find((stage) => stage.funnelId === funnelId && stage.name === name)?.color);
  /** Etapa atual do cadastro, pela participação ativa. */
  const currentStageName = (leadId: string | undefined) => {
    if (!leadId) return undefined;
    const membership = store.memberships.find((item) => item.leadId === leadId);
    return store.stages.find((stage) => stage.id === membership?.stageId)?.name;
  };
  const funnelParam = params.get("funil");
  const funnelId = funnelParam && funnels.some((funnel) => funnel.id === funnelParam) ? funnelParam : null;
  const selectedKey = params.get("aviso");

  const update = useCallback(
    (patch: Record<string, string | null>) => {
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          for (const [key, value] of Object.entries(patch)) {
            if (value === null || value === "") next.delete(key);
            else next.set(key, value);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  const today = civilDate(now, TIME_ZONE);
  // O feed inclui os resolvidos porque a aba "Resolvidas" precisa deles; as
  // demais abas descartam pelo filtro, não por uma segunda consulta.
  const feed = store.noticeFeed({ includeResolved: true });
  const preferences = readPreference(store.userPreferences ?? [], "notifications");
  const muted = useMemo(
    () => NOTICE_CATEGORIES.filter((item) => preferences.value.categories[item] === false),
    [preferences.value.categories],
  );

  const scope = { today, timeZone: TIME_ZONE, leads: store.leads };
  const visible = useMemo(
    () => filterNotices({ ...scope, notices: feed, view, category, funnelId, period, query }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [feed, view, category, funnelId, period, query, today, store.leads],
  );

  const summary = summarizeNotices(feed);
  const groups = useMemo(() => groupNoticesByDay(visible, today, TIME_ZONE), [visible, today]);
  const selected = selectedKey ? visible.find((notice) => notice.key === selectedKey) : undefined;
  const filtered =
    view !== DEFAULT_NOTICE_VIEW || category !== null || funnelId !== null || period !== "todos" || query !== "";

  /** Contagem de cada aba dentro da seleção corrente, menos a própria aba. */
  const tabCounts = useMemo(() => {
    const counts = {} as Record<NoticeViewId, number>;
    for (const item of NOTICE_VIEWS) {
      counts[item.id] = filterNotices({
        ...scope,
        notices: feed,
        view: item.id,
        category,
        funnelId,
        period,
        query,
      }).length;
    }
    return counts;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feed, category, funnelId, period, query, today, store.leads]);

  const categoryCounts = useMemo(() => {
    const counts = new Map<NoticeCategory, number>();
    for (const notice of feed) {
      if (notice.resolvedAt || notice.readAt) continue;
      counts.set(notice.category, (counts.get(notice.category) ?? 0) + 1);
    }
    return counts;
  }, [feed]);

  const funnelCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const notice of feed) {
      if (notice.resolvedAt || notice.readAt || !notice.funnelId) continue;
      counts.set(notice.funnelId, (counts.get(notice.funnelId) ?? 0) + 1);
    }
    return counts;
  }, [feed]);

  const toggleRead = useCallback(
    (notice: Notice) => {
      void store.markNoticeRead({ noticeKey: notice.key, read: !notice.readAt });
    },
    [store],
  );

  const resolve = useCallback(
    (notice: Notice) => {
      void store.resolveNotice({ noticeKey: notice.key, resolved: !notice.resolvedAt });
    },
    [store],
  );

  const markAll = useCallback(async () => {
    const keys = visible.filter((notice) => !notice.readAt).map((notice) => notice.key);
    if (keys.length === 0 || busy) return;
    setBusy(true);
    await store.markAllNoticesRead({ noticeKeys: keys });
    setBusy(false);
  }, [busy, store, visible]);

  const unreadInSelection = visible.filter((notice) => !notice.readAt).length;

  const sideGroups = [
    {
      // Sem rótulo, como no protótipo: a primeira coluna começa nas caixas.
      id: "caixas",
      items: [
        {
          id: "todas",
          label: "Caixa de entrada",
          count: summary.unread,
          icon: <Inbox size={15} aria-hidden="true" />,
        },
        ...NOTICE_CATEGORIES.map((item) => {
          const Icon = CATEGORY_ICONS[item];
          const silenced = muted.includes(item);
          return {
            id: item,
            label: silenced ? `${NOTICE_CATEGORY_LABEL[item]} (silenciada)` : NOTICE_CATEGORY_LABEL[item],
            count: categoryCounts.get(item) ?? 0,
            tone: CATEGORY_TONES[item],
            icon: silenced ? (
              <BellOff size={15} aria-hidden="true" />
            ) : (
              <Icon size={15} aria-hidden="true" />
            ),
          };
        }),
      ],
    },
    {
      id: "funis",
      label: "Funis",
      action: (
        <IconButton
          size="compact"
          variant="bare"
          label="Abrir a página de funis"
          icon={<Plus size={14} />}
          onClick={() => navigate(appPath("funis"))}
        />
      ),
      items: [
        { id: "todos", label: "Todos os funis", icon: <Layers size={15} aria-hidden="true" /> },
        ...funnels.map((funnel, index) => ({
          id: funnel.id,
          label: funnel.name,
          count: funnelCounts.get(funnel.id) ?? 0,
          tone: stageTone(index),
          // A inicial do funil num quadradinho da cor dele, como no protótipo.
          icon: (
            <i className="cx-space" aria-hidden="true">
              {funnel.name.trim().charAt(0).toUpperCase() || "?"}
            </i>
          ),
        })),
      ],
    },
  ];

  return (
    <div className="cx-layout cx-notificacoes">
      <SideNav
        label="Caixas e funis de notificações"
        groups={sideGroups}
        value={funnelId ?? category ?? "todas"}
        onChange={(id) => {
          if (id === "todas") update({ caixa: null, aviso: null });
          else if (NOTICE_CATEGORIES.includes(id as NoticeCategory)) update({ caixa: id, aviso: null });
          else update({ funil: id === "todos" ? null : id, aviso: null });
        }}
      />
      <section className="cx-work">
        <PageHead
          title="Notificações"
          sub={
            <>
              <b>{summary.total}</b> {summary.total === 1 ? "notificação" : "notificações"} ·{" "}
              <b>{summary.unread}</b> {summary.unread === 1 ? "não lida" : "não lidas"} ·{" "}
              <b>{summary.pending}</b> {summary.pending === 1 ? "pendente" : "pendentes"}
            </>
          }
          actions={
            <>
              <IconButton
                label="Verificar novas ocorrências"
                icon={<RefreshCw size={15} />}
                title="Recalcula o feed com o horário de agora."
                onClick={() => setNow(new Date().toISOString())}
              />
              <IconButton
                label="Preferências de notificações"
                icon={<Settings2 size={15} />}
                onClick={() => setSettingsOpen(true)}
              />
              <Button
                variant="secondary"
                icon={<CheckCheck size={15} aria-hidden="true" />}
                loading={busy}
                disabled={unreadInSelection === 0}
                title={
                  unreadInSelection === 0
                    ? "Não há aviso não lido nesta seleção."
                    : `Marca ${unreadInSelection} ${unreadInSelection === 1 ? "aviso" : "avisos"} desta seleção.`
                }
                onClick={() => void markAll()}
              >
                Marcar como lidas
              </Button>
            </>
          }
        />
        <Toolbar label="Filtros de notificações">
          <Tabs
            label="Visões da caixa"
            value={view}
            onChange={(id) => update({ aba: id === DEFAULT_NOTICE_VIEW ? null : id, aviso: null })}
            items={NOTICE_VIEWS.map((item) => {
              const Icon = VIEW_ICONS[item.id];
              return {
                id: item.id,
                label: item.label,
                count: tabCounts[item.id],
                icon: <Icon size={14} aria-hidden="true" />,
              };
            })}
          />
          <ToolbarSpacer />
          <CompactSelect
            prefix="Período"
            icon={<SlidersHorizontal size={13} aria-hidden="true" />}
            active={period !== "todos"}
            value={period}
            onChange={(value) => update({ periodo: value === "todos" ? null : value })}
            options={NOTICE_PERIODS.map((item) => ({ value: item.id, label: item.label }))}
          />
          {filtered && (
            <Button
              size="compact"
              variant="ghost"
              onClick={() =>
                update({ aba: null, caixa: null, funil: null, periodo: null, q: null, aviso: null })
              }
            >
              Limpar filtros
            </Button>
          )}
          <SearchInput
            label="Buscar por notificação, pessoa ou empresa"
            placeholder="Buscar notificações"
            value={query}
            onChange={(value) => update({ q: value })}
          />
        </Toolbar>

        {preferences.fellBack && (
          <p className="cx-notice-warning" role="status">
            As preferências gravadas não puderam ser lidas e os padrões estão em uso. Salve
            novamente para corrigir.
          </p>
        )}

        <div className={selected ? "cx-notices has-detail" : "cx-notices"}>
          <section className="cx-feed" aria-label="Lista de notificações">
            <p className="cx-sr-only" role="status">
              {visible.length} {visible.length === 1 ? "ocorrência" : "ocorrências"} na seleção
            </p>
            {visible.length === 0 ? (
              <EmptyState
                variant={filtered ? "no-results" : "empty"}
                icon={<Inbox size={22} aria-hidden="true" />}
                title={filtered ? "Nada nesta seleção" : "Você está em dia"}
                description={
                  filtered
                    ? "Nenhuma notificação corresponde aos filtros atuais."
                    : "Nenhuma tarefa vencida, fatura em aberto, lead parado ou incidente aguardando você."
                }
                action={
                  filtered ? (
                    <Button
                      variant="secondary"
                      onClick={() =>
                        update({
                          aba: null,
                          caixa: null,
                          funil: null,
                          periodo: null,
                          q: null,
                          aviso: null,
                        })
                      }
                    >
                      Limpar filtros
                    </Button>
                  ) : undefined
                }
              />
            ) : (
              groups.map((group) => (
                <section key={group.label} className="cx-date-group" aria-label={group.label}>
                  <h2 className="cx-date">
                    {group.label}
                    <span className="cx-date-count">{group.notices.length}</span>
                  </h2>
                  <div className="cx-date-list">
                    {group.notices.map((notice) => (
                      <NoticeRow
                        key={notice.key}
                        notice={notice}
                        today={today}
                        leadName={store.leads.find((lead) => lead.id === notice.leadId)?.name}
                        leadCompany={store.leads.find((lead) => lead.id === notice.leadId)?.company}
                        leadPhoto={store.leads.find((lead) => lead.id === notice.leadId)?.photoUrl}
                        stageColor={(name) => stageColorOf(notice.funnelId, name)}
                        selected={notice.key === selectedKey}
                        onSelect={() =>
                          update({ aviso: notice.key === selectedKey ? null : notice.key })
                        }
                        onToggleRead={() => toggleRead(notice)}
                        onResolve={() => resolve(notice)}
                        onOpenLead={
                          notice.leadId ? () => navigate(`${appPath("leads")}/${notice.leadId}`) : undefined
                        }
                      />
                    ))}
                  </div>
                </section>
              ))
            )}
          </section>

          {selected && (
            <NoticeDetail
              notice={selected}
              funnelName={funnels.find((funnel) => funnel.id === selected.funnelId)?.name}
              leadName={store.leads.find((lead) => lead.id === selected.leadId)?.name}
              leadCompany={store.leads.find((lead) => lead.id === selected.leadId)?.company}
              leadRole={store.leads.find((lead) => lead.id === selected.leadId)?.role}
              stageName={currentStageName(selected.leadId)}
              leadPhoto={store.leads.find((lead) => lead.id === selected.leadId)?.photoUrl}
              onClose={() => update({ aviso: null })}
              onToggleRead={() => toggleRead(selected)}
              onResolve={() => resolve(selected)}
              onOpenLead={
                selected.leadId ? () => navigate(`${appPath("leads")}/${selected.leadId}`) : undefined
              }
            />
          )}
        </div>
      </section>

      <PreferencesDialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        current={preferences.value}
        version={store.userPreferences?.find((item) => item.key === "notifications")?.version ?? 0}
      />
    </div>
  );
}

interface RowProps {
  notice: Notice;
  today: string;
  leadName?: string;
  leadCompany?: string;
  leadPhoto?: string;
  /** Cor da etapa pelo nome, no funil do aviso, para os selos de "avançou". */
  stageColor: (name: string) => string;
  selected: boolean;
  onSelect: () => void;
  onToggleRead: () => void;
  onResolve: () => void;
  onOpenLead?: () => void;
}

/**
 * Texto da linha como no protótipo (`cxNoticeText`): o nome em destaque, a
 * empresa discreta e o acontecimento; mudança de etapa vira os dois selos com
 * a seta entre eles.
 */
function NoticeText({
  detail,
  leadName,
  leadCompany,
  stageColor,
}: {
  detail: string;
  leadName?: string;
  leadCompany?: string;
  stageColor: (name: string) => string;
}) {
  let rest = detail;
  const parts: ReactNode[] = [];
  const sep = (key: string) => (
    <span key={key} className="cx-sep">
      ·
    </span>
  );
  // O detalhe que já cita o nome no meio (tarefas, recebimentos) fica como
  // está, sem repetir o nome na frente — é o que o protótipo faz.
  const repeats = Boolean(leadName && !detail.startsWith(leadName) && detail.includes(leadName));
  if (leadName && !repeats) {
    if (rest.startsWith(leadName)) rest = rest.slice(leadName.length).replace(/^\s*·\s*/, "");
    parts.push(<b key="nome">{leadName}</b>);
    if (leadCompany && rest.startsWith(leadCompany)) {
      rest = rest.slice(leadCompany.length).replace(/^\s*·\s*/, "");
      parts.push(sep("s1"), <span key="empresa">{leadCompany}</span>);
    }
    if (rest) parts.push(sep("s2"));
  }
  const stage = rest.match(/^Avançou (?:de (.+?) )?para (.+?)\.?$/);
  if (stage) {
    const pill = (name: string, key: string) => (
      <span key={key} className="cx-stage-inline" style={{ "--cx-tag": stageColor(name) } as CSSProperties}>
        <i aria-hidden="true" />
        {name}
      </span>
    );
    parts.push(<span key="verbo">{stage[1] ? "avançou de" : "avançou para"}</span>);
    if (stage[1]) {
      parts.push(pill(stage[1], "de"));
      parts.push(<ArrowRight key="seta" className="cx-arrow" aria-hidden="true" />);
    }
    parts.push(pill(stage[2], "para"));
  } else if (rest) {
    parts.push(<span key="texto">{rest}</span>);
  }
  return <div className="cx-nrow-text">{parts}</div>;
}

function NoticeRow({
  notice,
  today,
  leadName,
  leadCompany,
  leadPhoto,
  stageColor,
  selected,
  onSelect,
  onToggleRead,
  onResolve,
  onOpenLead,
}: RowProps) {
  const Icon = CATEGORY_ICONS[notice.category];
  const read = Boolean(notice.readAt);
  const name = noticeName(notice);
  return (
    <article
      aria-label={name}
      className={`cx-nrow${read ? " is-read" : " is-unread"}${selected ? " is-selected" : ""}`}
      style={{ "--cx-tone": SEVERITY_TONE[notice.severity] } as CSSProperties}
    >
      <div className="cx-nrow-content">
        <span className="cx-nrow-ico" title={NOTICE_CATEGORY_LABEL[notice.category]}>
          <Icon size={15} aria-hidden="true" />
          {!read && <i className="cx-unread" aria-hidden="true" />}
          <span className="cx-sr-only">
            {NOTICE_CATEGORY_LABEL[notice.category]}
            {read ? ", lida" : ", não lida"}
          </span>
        </span>
        <button
          type="button"
          className="cx-nrow-title"
          aria-expanded={selected}
          aria-label={`Detalhes de ${name}`}
          onClick={onSelect}
        >
          {notice.title}
        </button>
      </div>
      <div className="cx-nrow-preview">
        {leadName ? (
          <Avatar name={leadName} photoUrl={leadPhoto} className="cx-mini-ava" />
        ) : (
          <span className="cx-mini-ava cx-ava-sys" aria-hidden="true">
            <Icon size={11} />
          </span>
        )}
        <NoticeText detail={notice.detail} leadName={leadName} leadCompany={leadCompany} stageColor={stageColor} />
      </div>
      <div className="cx-nrow-end">
        {notice.resolvedAt ? (
          <span className="cx-tag is-resolved">
            <CheckCheck aria-hidden="true" />
            Resolvida
          </span>
        ) : notice.actionable ? (
          <span className="cx-tag is-pending">
            <AlertTriangle aria-hidden="true" />
            Pendente
          </span>
        ) : null}
        <time className="cx-nrow-time" dateTime={notice.occurredAt}>
          {stamp(notice, today)}
        </time>
        <div className="cx-nrow-actions">
          <IconButton
            size="compact"
            variant="bare"
            label={`${read ? "Marcar como não lida" : "Marcar como lida"}: ${name}`}
            icon={read ? <Mail size={15} /> : <Check size={15} />}
            onClick={onToggleRead}
          />
          {notice.actionable && !notice.resolvedAt && (
            <IconButton
              size="compact"
              variant="bare"
              label={`Confirmar resolução: ${name}`}
              icon={<CheckCheck size={15} />}
              onClick={onResolve}
            />
          )}
          {onOpenLead && (
            <IconButton
              size="compact"
              variant="bare"
              label={`Abrir o lead: ${name}`}
              icon={<UserRound size={15} />}
              onClick={onOpenLead}
            />
          )}
        </div>
      </div>
    </article>
  );
}

function NoticeDetail({
  notice,
  funnelName,
  leadName,
  leadCompany,
  leadRole,
  stageName,
  leadPhoto,
  onClose,
  onToggleRead,
  onResolve,
  onOpenLead,
}: {
  notice: Notice;
  funnelName?: string;
  leadName?: string;
  leadCompany?: string;
  leadRole?: string;
  stageName?: string;
  leadPhoto?: string;
  onClose: () => void;
  onToggleRead: () => void;
  onResolve: () => void;
  onOpenLead?: () => void;
}) {
  const Icon = CATEGORY_ICONS[notice.category];
  const read = Boolean(notice.readAt);
  // Os fatos do protótipo: o funil e a etapa atual do cadastro, e quando a
  // pendência foi resolvida.
  const facts: Array<[string, string]> = [["Funil", funnelName ?? "Não vinculado"]];
  if (leadName) facts.push(["Etapa atual", stageName ?? "Não informada"]);
  if (notice.resolvedAt)
    facts.push(["Resolvida em", fullFormat.format(new Date(notice.resolvedAt))]);

  return (
    <aside
      className="cx-detail"
      aria-label={`Detalhes de ${noticeName(notice)}`}
      style={{ "--cx-tone": SEVERITY_TONE[notice.severity] } as CSSProperties}
    >
      <header className="cx-detail-top">
        <span className="cx-tag">
          <Icon aria-hidden="true" />
          {NOTICE_CATEGORY_LABEL[notice.category]}
        </span>
        <IconButton size="compact" variant="bare" label="Fechar detalhes" icon={<X size={15} />} onClick={onClose} />
      </header>
      <div className="cx-detail-body">
        <h2 className="cx-detail-title" tabIndex={-1}>
          {notice.title}
        </h2>
        <p className="cx-detail-time">
          <Clock size={13} aria-hidden="true" />
          {fullFormat.format(new Date(notice.occurredAt))}
        </p>
        {notice.resolvedAt ? (
          <span className="cx-tag is-resolved cx-detail-status">
            <CheckCheck aria-hidden="true" />
            Resolvida
          </span>
        ) : notice.actionable ? (
          <span className="cx-tag is-pending cx-detail-status">
            <AlertTriangle aria-hidden="true" />
            Pendente
          </span>
        ) : (
          <span className="cx-tag is-info cx-detail-status">
            <Check aria-hidden="true" />
            Informativa
          </span>
        )}
        <p className="cx-detail-text">{notice.detail}</p>
        {leadName && (
          <div className="cx-person">
            <Avatar name={leadName} photoUrl={leadPhoto} />
            <div>
              <strong>{leadName}</strong>
              <small>{[leadCompany, leadRole].filter(Boolean).join(" · ")}</small>
            </div>
          </div>
        )}
        <dl className="cx-facts">
          {facts.map(([term, value]) => (
            <div key={term} className={term === "Resolvida em" ? "is-wide" : undefined}>
              <dt>{term}</dt>
              <dd>{value}</dd>
            </div>
          ))}
        </dl>
        <p className="cx-detail-note">
          {notice.resolvedAt
            ? "O acompanhamento foi encerrado. O registro continua no histórico."
            : notice.actionable
              ? "Marcar como lida não resolve esta pendência, e resolver não conserta a causa: trate a origem antes de confirmar."
              : "Ocorrência informativa: não exige confirmação de resolução."}
        </p>
      </div>
      <footer className="cx-detail-actions">
        {onOpenLead && (
          <Button variant="primary" className="cx-detail-main" icon={<UserRound size={15} aria-hidden="true" />} onClick={onOpenLead}>
            Abrir cadastro
          </Button>
        )}
        {notice.actionable && (
          <Button variant="ghost" className="cx-resolve" icon={<CheckCheck size={15} aria-hidden="true" />} onClick={onResolve}>
            {notice.resolvedAt ? "Reabrir acompanhamento" : "Confirmar resolução"}
          </Button>
        )}
        <Button variant="ghost" icon={read ? <Mail size={15} aria-hidden="true" /> : <Check size={15} aria-hidden="true" />} onClick={onToggleRead}>
          {read ? "Marcar não lida" : "Marcar como lida"}
        </Button>
      </footer>
    </aside>
  );
}

function PreferencesDialog({
  open,
  onClose,
  current,
  version,
}: {
  open: boolean;
  onClose: () => void;
  current: NotificationPreferences;
  version: number;
}) {
  const store = useStore();
  const [draft, setDraft] = useState<NotificationPreferences>(current);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setDraft(current);
    // `current` muda a cada render do pai; copiar só na abertura evita que uma
    // gravação concorrente apague o que está sendo editado.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const dirty = JSON.stringify(draft) !== JSON.stringify(current);

  const save = async () => {
    if (saving) return;
    setSaving(true);
    const result = await store.setPreference({
      key: "notifications",
      value: draft,
      expectedVersion: version,
    });
    setSaving(false);
    if (result.ok) onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      dirty={dirty}
      title="Preferências de notificações"
      description="Vale para o sino e para esta central. Silenciar uma categoria não apaga nada: os avisos voltam assim que ela for reativada."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="primary" loading={saving} onClick={() => void save()}>
            Salvar preferências
          </Button>
        </>
      }
    >
      <fieldset className="cx-pref-group">
        <legend>Categorias exibidas</legend>
        {NOTICE_CATEGORIES.map((category) => (
          <Switch
            key={category}
            checked={draft.categories[category] !== false}
            label={NOTICE_CATEGORY_LABEL[category]}
            onChange={(checked) =>
              setDraft((value) => ({
                ...value,
                categories: { ...value.categories, [category]: checked },
              }))
            }
          />
        ))}
      </fieldset>
      <fieldset className="cx-pref-group">
        <legend>Aviso flutuante ao chegar</legend>
        {NOTICE_CATEGORIES.map((category) => {
          const silenced = draft.categories[category] === false;
          return (
            <Switch
              key={category}
              checked={draft.banners[category] === true && !silenced}
              disabled={silenced}
              disabledReason="A categoria está silenciada; reative-a para receber o aviso flutuante."
              label={NOTICE_CATEGORY_LABEL[category]}
              onChange={(checked) =>
                setDraft((value) => ({
                  ...value,
                  banners: { ...value.banners, [category]: checked },
                }))
              }
            />
          );
        })}
      </fieldset>
      <p className="cx-pref-note">
        O aviso flutuante ainda não é exibido por nenhuma superfície: a preferência fica gravada e
        passa a valer quando a central em tempo real existir.
      </p>
    </Dialog>
  );
}

export default NotificacoesPage;
